// Driver navigation launcher. Builds a "directions" URL for the driver's
// preferred external maps app and opens it in a new tab. Called by
// `RideTrip.jsx` on Accept (→ pickup) and Start ride (→ drop-off).
//
// Preferences supported (Apple Maps intentionally excluded per product
// spec — Ghana driver community relies on Google Maps / Waze):
//   auto   → Google Maps (default everywhere).
//   google → Google Maps.
//   waze   → Waze.
//
// The window.open() must fire inside the same click gesture that
// triggered the state change so mobile Safari and Chrome don't block
// the popup. Callers open the tab BEFORE awaiting any backend request.

/**
 * Coord validity. Rejects null/undefined/NaN/non-finite/exact 0,0 (which
 * is the middle of the ocean and almost always a stale sentinel).
 */
const isValidCoord = (lat, lng) => {
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return false;
  if (Math.abs(la) < 0.0001 && Math.abs(ln) < 0.0001) return false;
  if (Math.abs(la) > 90 || Math.abs(ln) > 180) return false;
  return true;
};

/**
 * Build the directions URL for the given destination.
 * @param {number} lat
 * @param {number} lng
 * @param {"auto"|"google"|"waze"} pref
 * @param {{lat:number,lng:number}[]} [waypoints] Optional intermediate
 *   stops in order. Google Maps handles up to 9 waypoints via `waypoints`
 *   query param. Waze does not support waypoints — those fall back to
 *   just the final destination.
 * @param {string} [addressFallback] Human-readable address to use when
 *   coords are invalid (e.g. legacy bookings without lat/lng). Google
 *   Maps geocodes the string on open.
 * @returns {string|null} URL to open, or null if there's nothing to nav.
 */
export function buildNavUrl(lat, lng, pref = "auto", waypoints = [], addressFallback = "") {
  // Apple Maps is intentionally not supported — collapse any legacy or
  // unknown preference back to Google Maps.
  const resolved = pref === "waze" ? "waze" : "google";
  const validCoords = isValidCoord(lat, lng);
  const la = Number(lat);
  const ln = Number(lng);
  const address = (addressFallback || "").trim();
  if (!validCoords && !address) return null;

  const cleanWps = (waypoints || []).filter((w) => isValidCoord(w?.lat, w?.lng));

  if (resolved === "waze") {
    if (validCoords) {
      return `https://waze.com/ul?ll=${encodeURIComponent(`${la},${ln}`)}&navigate=yes`;
    }
    // Waze address fallback (best-effort — Waze does geocode `q=`).
    return `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
  }

  // `dir_action=navigate` starts turn-by-turn immediately when the Google
  // Maps app is installed (Android + iOS). Falls back to the standard
  // directions view on desktop / when the app is absent. `travelmode=
  // driving` locks the vehicle mode so the app doesn't second-guess
  // based on last-used mode.
  const dest = validCoords
    ? encodeURIComponent(`${la},${ln}`)
    : encodeURIComponent(address);
  let url = `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving&dir_action=navigate`;
  if (cleanWps.length) {
    url += `&waypoints=${cleanWps
      .map((w) => encodeURIComponent(`${Number(w.lat)},${Number(w.lng)}`))
      .join("%7C")}`;
  }
  return url;
}

/**
 * Immediately open the driver's preferred nav app to the given
 * destination. Must be called synchronously from a click handler.
 * Returns the window reference (or null if blocked / no valid target).
 * @param {number} lat
 * @param {number} lng
 * @param {"auto"|"google"|"apple"|"waze"} pref
 * @param {{lat:number,lng:number}[]} [waypoints]
 * @param {string} [addressFallback]
 */
export function launchNavigation(lat, lng, pref = "auto", waypoints = [], addressFallback = "") {
  const url = buildNavUrl(lat, lng, pref, waypoints, addressFallback);
  if (!url) return null;
  // 1st try: open in a new tab (works in most browsers when triggered
  // from a user click). Some in-app WebViews (Capacitor, Facebook,
  // Instagram) silently return `null` here — fall through to a
  // same-window navigation which reliably fires the intent so the
  // native Google Maps / Waze app takes over via URL scheme handling.
  let win = null;
  try {
    win = window.open(url, "_blank", "noopener,noreferrer");
  } catch (_e) { /* WebViews sometimes throw */ }
  if (!win) {
    try { window.location.href = url; } catch (_e) { /* noop */ }
    return true;
  }
  return win;
}

/**
 * Resolve the current navigation TARGET for a trip based on its status.
 *
 *   - `pending` / `accepted`  → the pickup pin (driver heading to rider)
 *   - `in_progress`           → the drop-off, with any stops as waypoints
 *   - anything else           → null (nothing to navigate to)
 *
 * Returns `{ lat, lng, waypoints, label }` or `null`. The `label`
 * describes what the target is ("pickup", "drop-off") so the caller
 * can render the correct button text.
 */
export function resolveNavTarget(trip) {
  if (!trip) return null;
  // Only the stops still ahead of the driver (stop_index = current leg).
  const stopsInOrder = (trip.stops || []).slice(Math.max(0, Number(trip.stop_index) || 0))
    .filter((s) => isValidCoord(s?.lat, s?.lng));
  // Runner jobs happen at a single location — pickup IS the destination
  // from accept through completion. Return the pickup pin regardless of
  // trip status so the "To job" button keeps working after the runner
  // starts (status flips to `in_progress`) and never falls back to a
  // non-existent drop-off.
  // Trash pickups are single-location too (the truck collects and leaves).
  if (((trip.rideshare_type || "").toLowerCase() === "runner" || trip.service_flavor === "trash")
      && ["pending", "accepted", "negotiating", "in_progress"].includes(trip.status)) {
    const hasCoords = isValidCoord(trip.pickup_lat, trip.pickup_lng);
    const address = (trip.pickup_address || "").trim();
    if (!hasCoords && !address) return null;
    return {
      lat: hasCoords ? Number(trip.pickup_lat) : null,
      lng: hasCoords ? Number(trip.pickup_lng) : null,
      waypoints: [],
      label: "job",
      address,
    };
  }
  if (["pending", "accepted", "negotiating"].includes(trip.status)) {
    const hasCoords = isValidCoord(trip.pickup_lat, trip.pickup_lng);
    const address = (trip.pickup_address || "").trim();
    if (!hasCoords && !address) return null;
    return {
      lat: hasCoords ? Number(trip.pickup_lat) : null,
      lng: hasCoords ? Number(trip.pickup_lng) : null,
      waypoints: [],
      label: "pickup",
      address,
    };
  }
  if (trip.status === "in_progress") {
    const hasCoords = isValidCoord(trip.dropoff_lat, trip.dropoff_lng);
    const address = (trip.dropoff_address || "").trim();
    if (!hasCoords && !address) return null;
    return {
      lat: hasCoords ? Number(trip.dropoff_lat) : null,
      lng: hasCoords ? Number(trip.dropoff_lng) : null,
      waypoints: stopsInOrder.map((s) => ({ lat: Number(s.lat), lng: Number(s.lng) })),
      label: stopsInOrder.length ? "next stop" : "drop-off",
      address,
    };
  }
  return null;
}

export const NAV_APP_LABELS = {
  auto: "Google Maps (default)",
  google: "Google Maps",
  waze: "Waze",
};

// Only these prefs are user-selectable. Legacy `apple` / `auto` values
// still stored on driver profiles are collapsed to Google Maps by
// `buildNavUrl`.
export const NAV_APP_CHOICES = ["google", "waze"];
